import { useState } from "react";
import icons from "enums/icons";
import CustomIcon from "components/CustomIcon";
import { Box, IconButton, ListItemText, Menu, MenuItem } from "@mui/material";

export type Order = "asc" | "desc";
export type OrderTable = "asc" | "desc" | "default";
export type OrderTableBy<D = OrderTable, C extends string | number = string | number> = { columnId: C; direction: D };
export type sortConfig = {
    isSortableByBestMatch?: boolean;
    sortBy?: "date" | "a-z" | "numeric" | "paceStatus" | "longest-shortest";
};

const SortMenu = ({
    columnId,
    currentSortBy,
    icon,
    label,
    sortConfig,
    onSort,
    onHideColumn,
    disabled,
}: {
    columnId: string | number;
    currentSortBy: OrderTableBy | null | undefined;
    icon: React.ReactNode;
    label?: React.ReactNode;
    sortConfig?: sortConfig;
    canSortByBestMatch?: boolean;
    canSortByDate?: boolean;
    onSort?: (direction: OrderTable, columnId: string | number) => void;
    onHideColumn?: (ev: React.MouseEvent<HTMLLIElement, MouseEvent>, id: string | number) => void;
    disabled?: boolean;
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const selectedOption = currentSortBy?.columnId === columnId;

    const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleSort = (order: OrderTable) => {
        onSort && onSort(order, columnId);
        handleCloseMenu();
    };

    const menuConfig = [
        ...(sortConfig?.isSortableByBestMatch
            ? [
                  {
                      label: "Best Match",
                      sortBy: "default",
                      icon: <CustomIcon sx={{ fontSize: 14, mr: 1 }} icon={icons.fire} />,
                      action: () => handleSort("default"),
                  },
              ]
            : []),

        ...(sortConfig?.sortBy === "numeric"
            ? [
                  {
                      label: "Sort Ascending",
                      sortBy: "asc",
                      icon: <CustomIcon sx={{ fontSize: 10, mr: 1.6 }} icon={icons.arrowUp} />,
                      action: () => handleSort("asc"),
                  },
                  {
                      label: "Sort Descending",
                      sortBy: "desc",
                      icon: (
                          <CustomIcon
                              sx={{ fontSize: 10, mr: 1.6, transform: "rotate(180deg)" }}
                              icon={icons.arrowUp}
                          />
                      ),
                      action: () => handleSort("desc"),
                  },
              ]
            : []),

        ...(sortConfig?.sortBy === "a-z"
            ? [
                  {
                      label: "Sort A - Z",
                      sortBy: "asc",
                      icon: (
                          <CustomIcon
                              sx={{ fontSize: 10, mr: 1.6, transform: "rotate(180deg)" }}
                              icon={icons.arrowUp}
                          />
                      ),
                      action: () => handleSort("asc"),
                  },
                  {
                      label: "Sort Z - A",
                      sortBy: "desc",
                      icon: <CustomIcon sx={{ fontSize: 10, mr: 1.6 }} icon={icons.arrowUp} />,
                      action: () => handleSort("desc"),
                  },
              ]
            : []),

        ...(sortConfig?.sortBy === "date"
            ? [
                  {
                      label: "Sort by Newest",
                      sortBy: "desc",
                      icon: (
                          <CustomIcon
                              sx={{ fontSize: 10, mr: 1.6, transform: "rotate(180deg)" }}
                              icon={icons.arrowUp}
                          />
                      ),
                      action: () => handleSort("desc"),
                  },
                  {
                      label: "Sort by Oldest",
                      sortBy: "asc",
                      icon: <CustomIcon sx={{ fontSize: 10, mr: 1.6 }} icon={icons.arrowUp} />,
                      action: () => handleSort("asc"),
                  },
              ]
            : []),

        ...(sortConfig?.sortBy === "paceStatus"
            ? [
                  {
                      label: "Sort by Completed",
                      sortBy: "desc",
                      icon: <CustomIcon sx={{ fontSize: 14, mr: 1 }} icon={icons.paceIconColouredDark} />,
                      action: () => handleSort("desc"),
                  },
                  {
                      label: "Sort by In Progress",
                      sortBy: "asc",
                      icon: <CustomIcon sx={{ fontSize: 14, mr: 1 }} icon={icons.paceIconGrayedOut} />,
                      action: () => handleSort("asc"),
                  },
              ]
            : []),

        ...(sortConfig?.sortBy === "longest-shortest"
            ? [
                  {
                      label: "Sort by Longest",
                      sortBy: "asc",
                      icon: <CustomIcon sx={{ fontSize: 8, mr: 1.6 }} icon={icons.arrowUp} />,
                      action: () => handleSort("asc"),
                  },
                  {
                      label: "Sort by Shortest",
                      sortBy: "desc",
                      icon: (
                          <CustomIcon sx={{ fontSize: 8, mr: 1.6, transform: "rotate(180deg)" }} icon={icons.arrowUp} />
                      ),
                      action: () => handleSort("desc"),
                  },
              ]
            : []),

        ...(onHideColumn !== undefined
            ? [
                  {
                      label: "Hide Column",
                      sortBy: "none",
                      icon: <CustomIcon sx={{ fontSize: 14, mr: 1 }} icon={icons.eyeStriked} />,
                      action: (ev: React.MouseEvent<HTMLLIElement, MouseEvent>) => onHideColumn(ev, columnId),
                  },
              ]
            : []),
    ];

    return (
        <>
            <IconButton disabled={disabled} size="small" onClick={handleOpenMenu}>
                <Box component="span">{icon}</Box>
                {label && <Box component="span">{label}</Box>}
            </IconButton>
            <Menu id="sorting-menu" anchorEl={anchorEl} open={open} onClose={handleCloseMenu}>
                {menuConfig.map((item) => {
                    const { label, sortBy, icon, action } = item;
                    return (
                        <MenuItem
                            key={`${label} - ${sortBy}`}
                            sx={(theme) => ({
                                background:
                                    selectedOption && currentSortBy.direction === sortBy
                                        ? theme.palette.background.blue
                                        : "",
                            })}
                            onClick={action}
                        >
                            {icon}
                            <ListItemText>{label}</ListItemText>
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
};

export default SortMenu;
